import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Select,
    Option,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import DeleteTimelineEventButton from '../../common/DeleteTimelineEventButton';
const { rootUrl } = require('../../../config/config');

const OwnerTimeline = ({ ownerCode, ownerName }) => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        event_type: '',
        plate_no: '',
        status: 'active'
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [cars, setCars] = useState([]);
    const [formData, setFormData] = useState({
        event_type: 'PAYMENT',
        plate_no: '',
        event_date: new Date().toISOString(),
        title: '',
        description: '',
        related_transaction_id: '',
        created_by: user?.username || ''
    });

    const fetchTimeline = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.event_type) params.append('event_type', filters.event_type);
            if (filters.plate_no) params.append('plate_no', filters.plate_no);
            if (filters.status) params.append('status', filters.status);

            const response = await axios.get(
                `${rootUrl}/api/owners/${ownerCode}/timeline?${params.toString()}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setTimeline(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching timeline:', error);
        } finally {
            setLoading(false);
        }
    }, [ownerCode, filters, authToken]);

    const fetchCars = useCallback(async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/cars?owner_code=${ownerCode}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (response.data) {
                setCars(response.data);
            }
        } catch (error) {
            console.error('Error fetching cars:', error);
        }
    }, [ownerCode, authToken]);

    useEffect(() => {
        if (ownerCode) {
            fetchTimeline();
            fetchCars();
        }
    }, [ownerCode, fetchTimeline, fetchCars]);

    const handleAddEntry = async () => {
        try {
            const response = await axios.post(
                `${rootUrl}/api/owners/${ownerCode}/timeline`,
                {
                    ...formData,
                    owner_code: ownerCode
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setShowAddModal(false);
                setFormData({
                    event_type: 'PAYMENT',
                    plate_no: '',
                    event_date: new Date().toISOString(),
                    title: '',
                    description: '',
                    related_transaction_id: '',
                    created_by: user?.username || ''
                });
                fetchTimeline();
            }
        } catch (error) {
            console.error('Error adding timeline entry:', error);
            alert('Error adding timeline entry: ' + (error.response?.data?.message || error.message));
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getEventTypeIcon = (type) => {
        const icons = {
            'PAYMENT': '💰',
            'REMITTANCE': '💵',
            'REIMBURSEMENT': '💸',
            'CHARGE': '📋',
            'NOTE': '📝',
            'ADJUSTMENT': '⚖️'
        };
        return icons[type] || '📌';
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardBody>
                    <div className="flex justify-between items-center mb-4">
                        <Typography variant="h5">{ownerName} - Timeline</Typography>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outlined"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                Filters
                            </Button>
                            <Button
                                size="sm"
                                color="blue"
                                onClick={() => setShowAddModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                Add Event
                            </Button>
                        </div>
                    </div>

                    {showFilters && (
                        <Card className="mb-4 p-4 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Select
                                    label="Event Type"
                                    value={filters.event_type}
                                    onChange={(val) => setFilters(prev => ({ ...prev, event_type: val }))}
                                >
                                    <Option value="">All Types</Option>
                                    <Option value="PAYMENT">Payment</Option>
                                    <Option value="REMITTANCE">Remittance</Option>
                                    <Option value="REIMBURSEMENT">Reimbursement</Option>
                                    <Option value="CHARGE">Charge</Option>
                                    <Option value="NOTE">Note</Option>
                                    <Option value="ADJUSTMENT">Adjustment</Option>
                                </Select>
                                <Select
                                    label="Car"
                                    value={filters.plate_no}
                                    onChange={(val) => setFilters(prev => ({ ...prev, plate_no: val }))}
                                >
                                    <Option value="">All Cars</Option>
                                    {cars.map(car => (
                                        <Option key={car.plate_no} value={car.plate_no}>
                                            {car.plate_no}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    size="sm"
                                    variant="text"
                                    onClick={() => {
                                        setFilters({ event_type: '', plate_no: '', status: 'active' });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                                    Clear Filters
                                </Button>
                            </div>
                        </Card>
                    )}

                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <div className="space-y-4">
                            {timeline.length === 0 ? (
                                <Typography className="text-center text-gray-500">
                                    No timeline entries found
                                </Typography>
                            ) : (
                                timeline.map((entry) => (
                                    <Card key={entry.id} className="p-4">
                                        <div className="flex items-start gap-4">
                                            <div className="text-3xl">
                                                {getEventTypeIcon(entry.event_type)}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <Typography variant="h6">
                                                            {entry.title}
                                                        </Typography>
                                                        <Typography variant="small" color="gray">
                                                            {formatDate(entry.event_date)}
                                                            {entry.plate_no && ` • ${entry.plate_no}`}
                                                        </Typography>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Typography
                                                            variant="small"
                                                            className={`px-2 py-1 rounded ${
                                                                entry.event_type === 'PAYMENT' ? 'bg-green-100 text-green-800' :
                                                                entry.event_type === 'CHARGE' ? 'bg-red-100 text-red-800' :
                                                                'bg-blue-100 text-blue-800'
                                                            }`}
                                                        >
                                                            {entry.event_type}
                                                        </Typography>
                                                        <DeleteTimelineEventButton
                                                            deleteUrl={`${rootUrl}/api/owners/${ownerCode}/timeline/${entry.id}`}
                                                            eventTitle={entry.title}
                                                            onDeleted={() => setTimeline((prev) => prev.filter((e) => e.id !== entry.id))}
                                                        />
                                                    </div>
                                                </div>
                                                {entry.description && (
                                                    <Typography variant="small" className="mt-2">
                                                        {entry.description}
                                                    </Typography>
                                                )}
                                                {entry.related_transaction_id && (
                                                    <Typography variant="small" color="blue" className="mt-1">
                                                        Transaction: {entry.related_transaction_id}
                                                    </Typography>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                ))
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Add Timeline Entry Modal */}
            <Dialog open={showAddModal} handler={() => setShowAddModal(false)} size="md">
                <DialogHeader>Add Timeline Event</DialogHeader>
                <DialogBody className="space-y-4">
                    <Select
                        label="Event Type *"
                        value={formData.event_type}
                        onChange={(val) => setFormData(prev => ({ ...prev, event_type: val }))}
                    >
                        <Option value="PAYMENT">Payment</Option>
                        <Option value="REMITTANCE">Remittance</Option>
                        <Option value="REIMBURSEMENT">Reimbursement</Option>
                        <Option value="CHARGE">Charge</Option>
                        <Option value="NOTE">Note</Option>
                        <Option value="ADJUSTMENT">Adjustment</Option>
                    </Select>

                    <Select
                        label="Car (Optional)"
                        value={formData.plate_no}
                        onChange={(val) => setFormData(prev => ({ ...prev, plate_no: val }))}
                    >
                        <Option value="">None (Owner-level)</Option>
                        {cars.map(car => (
                            <Option key={car.plate_no} value={car.plate_no}>
                                {car.plate_no}
                            </Option>
                        ))}
                    </Select>

                    <Input
                        type="datetime-local"
                        label="Event Date *"
                        value={formData.event_date ? new Date(formData.event_date).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, event_date: new Date(e.target.value).toISOString() }))}
                    />

                    <Input
                        label="Title *"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    />

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={4}
                        />
                    </div>

                    <Input
                        label="Related Transaction ID (Optional)"
                        value={formData.related_transaction_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, related_transaction_id: e.target.value }))}
                    />
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={() => setShowAddModal(false)}
                        className="mr-2"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </Button>
                    <Button
                        color="blue"
                        onClick={handleAddEntry}
                        disabled={!formData.title || !formData.event_type}
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add Event
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default OwnerTimeline;

